import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { KeyRound, Copy, ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';
import { useUpdateStatus } from '../hooks/useUpdateStatus';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LicensePage = () => {
  const [status, setStatus] = useState(null);
  const [cnpj, setCnpj] = useState('');
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [updatePwd, setUpdatePwd] = useState('');
  const { status: updateStatus, loading: updateBusy, forceCheck, approve, cancelApproval } = useUpdateStatus();

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/license/status`, { withCredentials: true });
      setStatus(data);
      setCnpj(data?.cnpj || '');
    } catch (e) { console.error(e); }
  };

  const forceCheckUpdate = async () => {
    setError('');
    const r = await forceCheck();
    if (r.ok) { setSuccess('Verificação concluída.'); setTimeout(() => setSuccess(''), 3000); }
    else setError(r.error);
  };

  const approveUpdate = async (e) => {
    e.preventDefault();
    setError('');
    const r = await approve(updatePwd);
    if (r.ok) {
      setUpdatePwd('');
      setSuccess('Atualização aprovada! Será executada em até 1h.');
      setTimeout(() => setSuccess(''), 5000);
    } else setError(r.error);
  };

  const doCancelApproval = async () => {
    setError('');
    const r = await cancelApproval();
    if (r.ok) { setSuccess('Aprovação cancelada.'); setTimeout(() => setSuccess(''), 3000); }
    else setError(r.error);
  };

  useEffect(() => { load(); }, []);

  const saveCnpj = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/license/cnpj`, { cnpj }, { withCredentials: true });
      setStatus(data);
      setSuccess('CNPJ salvo. Copie o fingerprint atualizado e envie ao fornecedor.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) { setError(err.response?.data?.detail || 'Erro ao salvar CNPJ'); }
    finally { setBusy(false); }
  };

  const activate = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/license/activate`, { license_blob: key.trim() }, { withCredentials: true });
      setStatus(data);
      setKey('');
      setSuccess('Licença ativada com sucesso!');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) { setError(err.response?.data?.detail || 'Erro ao ativar licença'); }
    finally { setBusy(false); }
  };

  const copyFp = async () => {
    try { await navigator.clipboard.writeText(status?.fingerprint || ''); setSuccess('Fingerprint copiado!'); setTimeout(() => setSuccess(''), 2000); } catch (e) { /* clipboard indisponível */ }
  };

  const isActive = !!status?.active;
  const days = status?.days_remaining;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4 space-y-4" data-testid="license-page">
        <div className="flex items-center gap-3 mb-2">
          <KeyRound className="text-emerald-600" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Licença do Sistema</h1>
            <p className="text-sm text-slate-500">Gerencie a chave de liberação da sua instalação local.</p>
          </div>
        </div>

        {success && <Alert data-testid="license-page-success"><AlertDescription>{success}</AlertDescription></Alert>}
        {error && <Alert variant="destructive" data-testid="license-page-error"><AlertDescription>{error}</AlertDescription></Alert>}

        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-slate-800">Status</h2>
            <Button variant="ghost" size="sm" onClick={load} data-testid="license-reload">
              <RefreshCw size={14} className="mr-1" /> Atualizar
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Situação</p>
              {isActive ? (
                <Badge className="bg-emerald-100 text-emerald-800 mt-1" data-testid="license-status-badge">
                  <ShieldCheck size={12} className="mr-1" /> ATIVA
                </Badge>
              ) : (
                <Badge variant="destructive" className="mt-1" data-testid="license-status-badge">
                  <ShieldAlert size={12} className="mr-1" /> INATIVA
                </Badge>
              )}
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Plano</p>
              <p className="font-bold text-slate-800 capitalize">{status?.plan || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Expira em</p>
              <p className={`font-bold ${days != null && days <= 7 ? 'text-amber-600' : 'text-slate-800'}`}>
                {status?.expires_at ? new Date(status.expires_at).toLocaleDateString('pt-BR') : '—'}
                {days != null && (days >= 0 ? ` (${days} dias)` : ` (vencida há ${Math.abs(days)} dias)`)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="font-bold text-slate-800 mb-3">CNPJ da Empresa</h2>
          <form onSubmit={saveCnpj} className="flex gap-2">
            <Input value={cnpj} onChange={(e) => setCnpj(e.target.value)} placeholder="00.000.000/0001-00" required data-testid="license-page-cnpj-input" />
            <Button type="submit" disabled={busy || cnpj.replace(/\D/g,'').length < 11} data-testid="license-page-cnpj-save">Salvar</Button>
          </form>
          {isActive && (
            <p className="text-xs text-amber-700 mt-2">⚠️ Alterar o CNPJ enquanto a licença estiver ativa não é permitido.</p>
          )}
        </Card>

        {status?.fingerprint && (
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-bold text-slate-800">Fingerprint da Máquina</h2>
              <Button variant="outline" size="sm" onClick={copyFp} data-testid="license-page-copy-fp">
                <Copy size={14} className="mr-1" /> Copiar
              </Button>
            </div>
            <div className="bg-slate-900 rounded p-4 font-mono text-xl text-emerald-400 tracking-widest text-center select-all" data-testid="license-page-fingerprint">
              {status.fingerprint}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Envie este código ao fornecedor via WhatsApp/e-mail para receber uma chave nova.
            </p>
          </Card>
        )}

        <Card className="p-5">
          <h2 className="font-bold text-slate-800 mb-3">Ativar / Renovar Chave</h2>
          <form onSubmit={activate} className="space-y-2">
            <Label>Cole aqui a chave recebida do fornecedor</Label>
            <textarea
              value={key}
              onChange={(e) => setKey(e.target.value)}
              rows={6}
              placeholder="Cole a chave..."
              className="w-full border rounded p-2 text-xs font-mono bg-slate-50"
              data-testid="license-page-key-input"
            />
            <Button type="submit" disabled={busy || key.trim().length < 20} className="bg-emerald-600 hover:bg-emerald-700" data-testid="license-page-activate">
              <KeyRound size={16} className="mr-2" /> {busy ? 'Ativando...' : 'Ativar / Renovar Licença'}
            </Button>
          </form>
        </Card>

        {/* Atualização do sistema */}
        {updateStatus && (
          <Card className="p-5" data-testid="update-card">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <RefreshCw className="text-blue-600" size={20} />
                <h2 className="font-bold text-slate-800">Atualização do Sistema</h2>
              </div>
              <div className="flex items-center gap-2">
                {updateStatus.pending_approval ? (
                  <Badge className="bg-amber-500 text-white">AGUARDANDO EXECUÇÃO</Badge>
                ) : updateStatus.update_available ? (
                  <Badge className="bg-blue-600 text-white">NOVA VERSÃO</Badge>
                ) : (
                  <Badge className="bg-slate-500 text-white">EM DIA</Badge>
                )}
                <Button variant="outline" size="sm" onClick={forceCheckUpdate} disabled={updateBusy} data-testid="update-force-check">
                  <RefreshCw size={14} className="mr-1" /> Verificar agora
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Instalada</p>
                <p className="font-mono">{updateStatus.current_version}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Disponível</p>
                <p className={`font-bold ${updateStatus.update_available ? 'text-blue-600' : 'text-slate-800'}`}>
                  {updateStatus.latest_version || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Última verificação</p>
                <p>{updateStatus.last_check_at ? new Date(updateStatus.last_check_at).toLocaleString('pt-BR') : 'Nunca'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Canal</p>
                <p className="capitalize">{updateStatus.channel}</p>
              </div>
            </div>
            {updateStatus.changelog && (
              <div className="mt-3 p-3 bg-slate-50 rounded text-xs whitespace-pre-wrap max-h-40 overflow-y-auto" data-testid="update-changelog">
                <div className="font-bold text-slate-700 mb-1">Novidades</div>
                {updateStatus.changelog}
              </div>
            )}

            {updateStatus.update_available && !updateStatus.pending_approval && (
              <form onSubmit={approveUpdate} className="mt-4 space-y-2" data-testid="update-approve-form">
                <div className="bg-blue-50 border border-blue-300 rounded p-3 text-xs text-blue-900">
                  Aprovar a atualização vai executar backup automático + rebuild + reinício.
                  O processo é executado pelo Windows Task Scheduler em até 1 hora.
                  Informe sua senha de admin para confirmar.
                </div>
                <Label>Sua senha de administrador</Label>
                <Input
                  type="password"
                  value={updatePwd}
                  onChange={(e) => setUpdatePwd(e.target.value)}
                  data-testid="update-password"
                  required
                />
                <Button type="submit" disabled={updateBusy || !updatePwd} className="bg-blue-600 hover:bg-blue-700" data-testid="update-approve-btn">
                  <RefreshCw size={16} className="mr-2" /> {updateBusy ? 'Enviando...' : `Aprovar Atualização para ${updateStatus.latest_version}`}
                </Button>
              </form>
            )}

            {updateStatus.pending_approval && (
              <div className="mt-4 space-y-2">
                <div className="bg-amber-50 border border-amber-300 rounded p-3 text-xs text-amber-900">
                  ⏳ Atualização aprovada por <strong>{updateStatus.approved_by}</strong>.
                  Executará automaticamente na próxima janela do agendador (até 1 hora).
                  Se precisar cancelar, faça agora — depois de rodar não tem volta.
                </div>
                <Button variant="outline" onClick={doCancelApproval} disabled={updateBusy} data-testid="update-cancel-btn">
                  Cancelar aprovação
                </Button>
              </div>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default LicensePage;
